import { ApiError, ApiErrorKind } from './apiError';
import { notifySuccess } from '../notifications/notifySuccess';
import { getSuccessMessage } from '../notifications/successMessages';

function classifyFromStatus(status) {
  if (status === 401 || status === 403) return ApiErrorKind.Unauthorized;
  if (status === 404) return ApiErrorKind.NotFound;
  if (status === 409) return ApiErrorKind.Conflict;
  if (status === 400 || status === 415 || status === 422) return ApiErrorKind.Validation;
  if (status >= 500) return ApiErrorKind.Server;
  return ApiErrorKind.Unknown;
}

function isRetryable(kind, status) {
  if (kind === ApiErrorKind.Network) return true;
  if (kind === ApiErrorKind.Server) return true;
  if (status === 429) return true;
  return false;
}

function extractServerMessage(body, fallbackText) {
  if (!body) return fallbackText || '';

  // FastAPI typically: { detail: "..." } or { detail: [...] }
  if (typeof body === 'object' && typeof body.detail === 'string') return body.detail;

  // Pydantic-style details can be arrays of objects; keep it short + safe.
  if (typeof body === 'object' && Array.isArray(body.detail) && body.detail.length > 0) {
    const first = body.detail[0];
    if (first && typeof first.msg === 'string') return first.msg;
  }

  if (typeof body === 'string') return body;
  if (fallbackText) return fallbackText;

  return '';
}

async function safeParseBody(response) {
  const text = await response.text().catch(() => '');
  if (!text) return { body: null, text: '' };
  try {
    return { body: JSON.parse(text), text };
  } catch {
    return { body: null, text };
  }
}

/**
 * @param {object} args
 * @param {string} args.url Full URL
 * @param {string} args.operation Stable operation key (OP.*)
 * @param {string} [args.method]
 * @param {Record<string, any>} [args.query]
 * @param {Record<string, string>} [args.headers]
 * @param {any} [args.body]
 * @param {typeof fetch} [args.fetchFn]
 * @param {RequestCache} [args.cache]
 * @param {'json'|'text'|'raw'} [args.parseAs]
 * @returns {Promise<any>}
 */
export async function apiRequest({
  url,
  operation,
  method = 'GET',
  query,
  headers,
  body,
  fetchFn,
  cache,
  parseAs = 'json',
  showSuccessToast = true,
}) {
  let finalUrl = url;
  try {
    if (query && typeof query === 'object') {
      const u = new URL(url, window.location.origin);
      Object.entries(query).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        u.searchParams.set(k, String(v));
      });
      finalUrl = u.toString();
    }
  } catch {
    // If URL constructor fails (shouldn't for full URLs), fall back to raw.
    finalUrl = url;
  }

  const finalHeaders = { ...(headers || {}) };
  let finalBody = body;

  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const isBodyObject =
    body &&
    typeof body === 'object' &&
    !isFormData &&
    !(body instanceof Blob) &&
    !(body instanceof ArrayBuffer);

  if (isBodyObject) {
    finalBody = JSON.stringify(body);
    if (!finalHeaders['Content-Type']) finalHeaders['Content-Type'] = 'application/json';
  }

  let response;
  const doFetch = fetchFn || fetch;
  try {
    response = await doFetch(finalUrl, {
      method,
      headers: finalHeaders,
      body: method === 'GET' || method === 'HEAD' ? undefined : finalBody,
      credentials: 'include',
      ...(cache ? { cache } : {}),
    });
  } catch (err) {
    const kind = ApiErrorKind.Network;
    throw new ApiError({
      operation,
      kind,
      status: null,
      serverMessage: 'Network error',
      url: finalUrl,
      method,
      retryable: isRetryable(kind, null),
      cause: err,
    });
  }

  if (response.ok) {
    let data;

    if (parseAs === 'raw') {
      data = response;
    } else if (parseAs === 'text') {
      data = await response.text();
    } else if (response.status === 204) {
      data = null;
    } else {
      // Default: json
      data = await response.json().catch(() => null);
    }

    if (showSuccessToast !== false) {
      const successMessage = getSuccessMessage(operation);
      if (successMessage) {
        notifySuccess(successMessage);
      }
    }

    return data;
  }

  const { body: parsedBody, text } = await safeParseBody(response);
  const kind = classifyFromStatus(response.status);
  const serverMessage = extractServerMessage(parsedBody, text || response.statusText || '');

  throw new ApiError({
    operation,
    kind,
    status: response.status,
    serverMessage,
    url: finalUrl,
    method,
    responseBody: parsedBody,
    retryable: isRetryable(kind, response.status),
  });
}

